import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Music2, Plus, Power } from 'lucide-react'

import { ApiError } from '@/api/client'
import { instrumentsApi } from '@/api/endpoints'
import type { Instrument } from '@/api/types'
import { PageHeader } from '@/components/layout/AppLayout'
import { Alert, EmptyState } from '@/components/ui/Alert'
import { StatusBadge } from '@/components/ui/Badge'
import { Button, IconButton } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { TextField } from '@/components/ui/Field'
import { ConfirmDialog, Modal } from '@/components/ui/Modal'
import { SkeletonRows } from '@/components/ui/Spinner'
import {
  DataTable,
  EmptyRow,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@/components/ui/Table'
import { useToast } from '@/components/ui/Toast'
import { ToggleChip } from '@/components/ui/Toolbar'

import { PALETTE_VALUES } from '@/lib/palette'

const KOLORY = PALETTE_VALUES

export function InstrumentsPage() {
  const queryClient = useQueryClient()
  const { notifySuccess, notifyError } = useToast()

  const [includeInactive, setIncludeInactive] = useState(false)
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [name, setName] = useState('')
  const [color, setColor] = useState(KOLORY[0])
  const [formError, setFormError] = useState<string | null>(null)
  const [toDeactivate, setToDeactivate] = useState<Instrument | null>(null)

  const query = useQuery({
    queryKey: ['instruments', { includeInactive }],
    queryFn: () => instrumentsApi.list(includeInactive),
  })

  const create = useMutation({
    mutationFn: () => instrumentsApi.create({ name: name.trim(), color }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['instruments'] })
      notifySuccess('Instrument został dodany.')
      setIsFormOpen(false)
      setName('')
    },
    onError: (caught: unknown) => {
      setFormError(caught instanceof ApiError ? caught.message : 'Nie udało się dodać instrumentu.')
    },
  })

  const deactivate = useMutation({
    mutationFn: (instrument: Instrument) => instrumentsApi.deactivate(instrument.id),
    onSuccess: async (_, instrument) => {
      await queryClient.invalidateQueries({ queryKey: ['instruments'] })
      notifySuccess(`Instrument „${instrument.name}” został wycofany.`)
      setToDeactivate(null)
    },
    onError: (caught: unknown) => {
      notifyError(caught instanceof ApiError ? caught.message : 'Nie udało się wycofać.')
      setToDeactivate(null)
    },
  })

  const items = query.data ?? []

  return (
    <>
      <PageHeader
        title="Instrumenty"
        description="Słownik wspólny dla całej szkoły. Kolory rozróżniają zajęcia w grafiku."
        breadcrumb={['System', 'Instrumenty']}
        action={
          <Button
            icon={<Plus size={18} />}
            onClick={() => {
              setFormError(null)
              setIsFormOpen(true)
            }}
          >
            Nowy instrument
          </Button>
        }
      />

      <div className="mb-6">
        <ToggleChip
          checked={includeInactive}
          onChange={setIncludeInactive}
          label="Pokaż wycofane"
        />
      </div>

      {query.isError && (
        <Alert tone="error" className="mb-6">
          Nie udało się wczytać słownika instrumentów.
        </Alert>
      )}

      <Card>
        {query.isPending ? (
          <SkeletonRows />
        ) : items.length === 0 ? (
          <EmptyState
            icon={<Music2 size={28} />}
            title="Słownik jest pusty"
            description="Dodaj instrumenty, aby móc przypisywać je uczniom i nauczycielom."
          />
        ) : (
          <DataTable caption="Słownik instrumentów">
            <TableHead
              columns={[
                'Instrument',
                'Identyfikator',
                'Kolejność',
                'Status',
                { label: 'Akcje', srOnly: true },
              ]}
            />
            <TableBody>
              {items.map((instrument) => (
                <TableRow key={instrument.id} muted={!instrument.is_active}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <span
                        className="h-6 w-6 shrink-0 rounded-full"
                        style={{ backgroundColor: instrument.color }}
                        aria-hidden="true"
                      />
                      <span className="font-bold text-on-surface">{instrument.name}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-xs text-on-surface-variant">{instrument.slug}</span>
                  </TableCell>
                  <TableCell>{instrument.sort_order}</TableCell>
                  <TableCell>
                    <StatusBadge isActive={instrument.is_active} />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      label={`Wycofaj instrument ${instrument.name}`}
                      icon={<Power size={16} />}
                      onClick={() => setToDeactivate(instrument)}
                      disabled={!instrument.is_active}
                      className="hover:bg-error-container hover:text-error"
                    />
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 && <EmptyRow colSpan={5} message="Brak instrumentów." />}
            </TableBody>
          </DataTable>
        )}
      </Card>

      <Modal
        open={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        title="Nowy instrument"
        description="Identyfikator powstanie automatycznie z nazwy."
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setIsFormOpen(false)}>
              Anuluj
            </Button>
            <Button
              isLoading={create.isPending}
              disabled={name.trim() === ''}
              onClick={() => create.mutate()}
            >
              Dodaj
            </Button>
          </>
        }
      >
        <div className="space-y-5">
          {formError && <Alert tone="error">{formError}</Alert>}

          <TextField
            label="Nazwa"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="np. Klarnet"
            required
            autoFocus
          />

          <fieldset>
            <legend className="mb-2 text-sm font-semibold">Kolor plakietki</legend>
            <div className="flex flex-wrap gap-2">
              {KOLORY.map((option) => (
                <label key={option} className="cursor-pointer">
                  <input
                    type="radio"
                    name="kolor"
                    value={option}
                    checked={color === option}
                    onChange={() => setColor(option)}
                    className="peer sr-only"
                  />
                  <span
                    className="block h-9 w-9 rounded-full ring-offset-2 transition-all peer-checked:ring-2 peer-checked:ring-on-surface peer-focus-visible:ring-2"
                    style={{ backgroundColor: option }}
                    aria-label={`Kolor ${option}`}
                  />
                </label>
              ))}
            </div>
          </fieldset>
        </div>
      </Modal>

      <ConfirmDialog
        open={toDeactivate !== null}
        title="Wycofać instrument?"
        message={
          toDeactivate
            ? `„${toDeactivate.name}” zniknie z formularzy. Uczniowie i nauczyciele już do niego przypisani zachowają to przypisanie.`
            : ''
        }
        confirmLabel="Wycofaj"
        isLoading={deactivate.isPending}
        onConfirm={() => toDeactivate && deactivate.mutate(toDeactivate)}
        onClose={() => setToDeactivate(null)}
      />
    </>
  )
}
